/**
 * The other resolution path: the shipment details are fine, the shipping option is not. The
 * merchant moves the RMA onto another return method, picks one of that method's rates and
 * approves - the label is regenerated on the new option, the addresses and the parcel untouched.
 * A method no carrier quotes has no rates to pick: it is approved on its own settings.
 */
import { observer } from "mobx-react";
import FontBody1 from "../../../../../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../../../components/fonts/font-props";
import { FormField } from "../../../../../../../components/form-field/form-field";
import IconInfo from "../../../../../../../components/icons/icon-info/icon-info";
import Icon from "../../../../../../../components/icons/icon/icon";
import { Button } from "../../../../../../../components/inputs/button/button";
import { IOption, SelectDropdown } from "../../../../../../../components/inputs/select-dropdown/select-dropdown";
import ListLayout, { LIST_LAYOUT_SPACING_VARIANT } from "../../../../../../../components/layouts/list-layout/list-layout";
import RoundLoader, { RoundLoaderType } from "../../../../../../../components/loaders/round-loader/round-loader";
import { Note, NOTE_TYPE } from "../../../../../../../components/note/note";
import { IResolveLabelErrorScreenProps } from "../../../../stores/rma-action-store/resolve-label-error-action-store";
import ChangeShippingOptionRates from "./change-shipping-option-rates/change-shipping-option-rates";
import "./change-shipping-option-screen.scss";

const RETURN_METHOD_HINT = "Changing return method will change shipping details";

const NO_RETURN_METHODS_MESSAGE = "No eligible return methods are available for this shipment.";

const OWN_SETTINGS_MESSAGE =
    "This return method has no shipping rates to choose from. The RMA will be approved on the method's own settings.";

const ChangeShippingOptionScreen: React.FC<IResolveLabelErrorScreenProps> = observer(({ store }) => {
    const changeStore = store.changeShippingOption;
    /** The list and its states belong to the prefetch, which the other path owns. */
    const prefetch = store.validateShippingDetails;

    const returnMethods = store.returnMethods;
    const returnMethodOptions: IOption[] = returnMethods.map((returnMethod) => ({
        value: returnMethod.id,
        label: returnMethod.internal_name,
    }));
    const selectedReturnMethod = returnMethodOptions.find(
        ({ value }) => value === changeStore.selectedReturnMethodId,
    );

    /** Picking the method already chosen would re-quote it for nothing. */
    const selectReturnMethod = (option: IOption | null): void => {
        const returnMethod = returnMethods.find(({ id }) => id === option?.value);

        if (returnMethod && returnMethod.id !== changeStore.selectedReturnMethodId) {
            void changeStore.selectReturnMethod(returnMethod);
        }
    };

    return (
        <div className="change-shipping-option-screen">
            <ListLayout>
                <ListLayout spacingVariant={LIST_LAYOUT_SPACING_VARIANT.SMAll}>
                    <FontBody1 color={FONT_COLOR.NEUTRAL3}>
                        We have encountered an error while generating your label:
                    </FontBody1>

                    {store.errorNote && (
                        <Note caption={store.errorNote.caption} body={store.errorNote.body} />
                    )}
                </ListLayout>

                {prefetch.isLoading && (
                    <div className="change-shipping-option-screen-loader">
                        <RoundLoader type={RoundLoaderType.Dark} />
                    </div>
                )}

                {prefetch.loadFailed && (
                    <Note
                        type={NOTE_TYPE.ERROR}
                        caption="Could not load the return methods"
                        body="Close this window and open it again to retry."
                    />
                )}

                {/** Only the methods matter here, so the screen waits for them and not for the
                  * whole prefetch: an RMA with none of them has nothing to offer on this path. */}
                {!prefetch.isLoading && !prefetch.loadFailed && returnMethodOptions.length === 0 && (
                    <Note type={NOTE_TYPE.WARNING} body={NO_RETURN_METHODS_MESSAGE} />
                )}

                {returnMethodOptions.length > 0 && (
                    <>
                        <div className="change-shipping-option-screen-title">
                            <FontBody1 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>
                                Shipping options:
                            </FontBody1>

                            <div className="change-shipping-option-screen-hint">
                                <Icon Component={IconInfo} color="var(--color-accent-1)" />
                                <FontBody2 color={FONT_COLOR.ACCENT1}>{RETURN_METHOD_HINT}</FontBody2>
                            </div>
                        </div>

                        <FormField label="Return method">
                            <SelectDropdown
                                options={returnMethodOptions}
                                value={selectedReturnMethod}
                                onChange={selectReturnMethod}
                                isSearchable={false}
                                placeholder="Select a return method"
                            />
                        </FormField>

                        <div className="change-shipping-option-screen-divider" />

                        {/** Nothing to quote on a method no carrier prices: it says so in place of the rates. */}
                        {selectedReturnMethod && !changeStore.isQuotedMethod
                            ? <Note body={OWN_SETTINGS_MESSAGE} />
                            : (
                                <FormField label="Available shipping options for the selected return method">
                                    <ChangeShippingOptionRates store={changeStore} />
                                </FormField>
                            )}

                        <div className="resolve-label-error-modal-actions">
                            <Button
                                text="Approve RMA"
                                hasLoader
                                isLoading={changeStore.isApproving}
                                disabled={!changeStore.canApprove}
                                onClick={() => { void store.approve(); }}
                            />
                        </div>
                    </>
                )}
            </ListLayout>
        </div>
    );
});

export default ChangeShippingOptionScreen;
